/* eslint-disable class-methods-use-this */
import { deleteCookie, getCookie, setCookie } from 'cookies-next'
import router from 'next/router'
import { v4 as uuidv4 } from 'uuid'
import { getAlpha2WithExceptions } from '@/components/Checkout/_helpers/functions/getAlpha2WithExceptions'
import type { Alpha2CountryCode } from '@/lib/types/alpha2CountryCode'
import type { CosmicVariationType } from '@/lib/cms/cosmic/types/CosmicProductType'
import { datadogRum } from '@datadog/browser-rum'

import { MixpanelEvent } from './mixpanelEvent'
import type {
  MixpanelCartData,
  MixpanelProductData,
} from './helpers/generateCartEventData'

type ReferrerData = {
  $referrer: string
  $referring_domain: string
}

class MixpanelService {
  private static instance: MixpanelService

  private static readonly UTM_KEYS = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_content',
    'utm_term',
    'utm_id',
    'utm_source_platform',
    'utm_campaign_id',
    'utm_creative_format',
    'utm_marketing_tactic',
  ] as const

  private previousPath = ''

  private superProperties: Record<string, string> = {}

  private deviceId: string

  private userId: string | null = null

  private isInitialized = false

  private constructor() {
    this.deviceId = this.initializeDeviceId()
    this.initialize()
  }

  public static getInstance(): MixpanelService {
    if (!MixpanelService.instance) {
      MixpanelService.instance = new MixpanelService()
    }
    return MixpanelService.instance
  }

  private initializeDeviceId(): string {
    const storedDeviceId = getCookie('device_id') as string | undefined
    if (storedDeviceId) {
      return storedDeviceId
    }
    const newDeviceId = uuidv4()
    setCookie('device_id', newDeviceId, {
      maxAge: 60 * 60 * 24 * 365,
      path: '/',
    })
    return newDeviceId
  }

  public initialize(): void {
    this.isInitialized = true
    this.initializeSuperProperties()

    // For tracking programmatic navigation
    router.events.on('routeChangeStart', () => {
      if (this.isInitialized) {
        this.previousPath = window.location.href
      }
    })
  }

  public trackPageView(): void {
    if (!('unicity_product' in this.superProperties)) {
      this.initializeSuperProperties()
    }
    if (this.isInitialized) {
      this.trackEvent(MixpanelEvent.PAGE_VIEW, {
        ...this.getReferrerData(),
      }).catch(() => {})
    }
  }

  public async trackEvent(
    event: string,
    properties?: Record<
      string,
      | string
      | string[]
      | boolean
      | MixpanelCartData[]
      | MixpanelProductData[]
      | CosmicVariationType
      | number
    >,
  ): Promise<void> {
    const referrerData = this.getReferrerData()

    const eventProperties = {
      ...this.superProperties,
      ...properties,
      datadog_session_url: this.getDatadogSessionUrl(),
      ...this.getInitialReferrerProperties(),
      ...referrerData,
      $device_id: this.deviceId,
      ...this.getBrowserMetadata(),
      ...(this.userId && { $user_id: this.userId }),
    }
    await this.sendToApi('trackEvent', event, eventProperties)
  }

  public registerSuperProperties(properties: Record<string, string>): void {
    this.superProperties = { ...this.superProperties, ...properties }
  }

  public initializeSuperProperties(): void {
    this.registerSuperProperties({
      unicity_product: 'shop2',
      user_market: getAlpha2WithExceptions({
        alpha2: (getCookie('country') || 'US') as Alpha2CountryCode,
      }),
      user_market_extended: getCookie('country') || 'US',
      user_language: getCookie('language') || 'en',
      is_user_logged_in: getCookie('is_user_logged_in') || 'false',
      ...this.getUtmParameters(),
    })

    if (getCookie('user_type')) {
      this.registerSuperProperties({
        user_type: getCookie('user_type') || 'Customer',
      })
    }
  }

  public identify(userId: string): void {
    this.userId = userId
  }

  public async setPeopleProperties(
    properties: Record<string, string>,
  ): Promise<void> {
    if (!this.userId) {
      throw new Error('Cannot set people properties without a user ID.')
    }

    const peopleProperties = {
      $user_id: this.userId,
      ...properties,
    }
    await this.sendToApi('setPeopleProperties', null, peopleProperties)
  }

  public reset(): void {
    this.resetSuperProperties()
    this.userId = null
    deleteCookie('device_id')
    this.deviceId = this.initializeDeviceId()
    deleteCookie('initial_referrer')
    deleteCookie('initial_referring_domain')
    deleteCookie('user_type')
    deleteCookie('userInitilizedInMixpanel')
  }

  private async sendToApi(
    action: string,
    event: string | null,
    properties: Record<
      string,
      | string
      | string[]
      | boolean
      | MixpanelCartData[]
      | MixpanelProductData[]
      | number
    >,
  ): Promise<void> {
    try {
      const response = await fetch('/api/mixpanel', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Access-Control-Allow-Origin': '*',
        },
        body: JSON.stringify({ action, event, properties }),
      })
      if (!response.ok)
        console.error(`Failed to ${action}:`, response.statusText)
    } catch (error) {
      console.error(`Error in ${action}:`, error)
    }
  }

  private resetSuperProperties(): void {
    this.superProperties = {}
  }

  private getDatadogSessionUrl(): string {
    const startTime = Date.now()
    const internalContext = datadogRum.getInternalContext()
    // Try different ways to access the session ID

    if (internalContext?.session_id) {
      return `https://us5.datadoghq.com/rum/replay/sessions/${internalContext.session_id}?applicationId=${process.env.NEXT_PUBLIC_DATADOG_APPLICATION_ID}&from=${startTime}`
    }

    return ''
  }

  private getBrowserMetadata(): Record<string, string | number> {
    if (typeof window === 'undefined') return {}

    return {
      $screen_height: window.screen.height,
      $screen_width: window.screen.width,
      $current_url: window.location.href,
    }
  }

  public getInitialReferrerProperties(): Record<string, string> {
    const initialReferrer = getCookie('initial_referrer') as string | null
    const initialReferringDomain = getCookie('initial_referring_domain') as
      | string
      | null
    return {
      $initial_referrer: initialReferrer || '$direct',
      $initial_referring_domain: initialReferringDomain || '$direct',
    }
  }

  private getUtmParameters(): Record<string, string> {
    if (typeof window === 'undefined') return {}

    const url = new URL(window.location.href)
    const params: Record<string, string> = {}

    for (const param of MixpanelService.UTM_KEYS) {
      const value = url.searchParams.get(param)
      if (value) {
        params[param] = value
      }
    }

    return params
  }

  public setInitialUtmProperties(): Record<string, string> {
    // eslint-disable-next-line unicorn/no-array-reduce
    return MixpanelService.UTM_KEYS.reduce((accumulator, key) => {
      const value = this.superProperties[key]
      if (value && value.trim() !== '') {
        return {
          ...accumulator,
          [`initial_${key}`]: value,
        }
      }
      return accumulator
    }, {})
  }

  private parseReferrerUrl(url: string): ReferrerData | false {
    try {
      return {
        $referrer: url,
        $referring_domain: new URL(url).host,
      }
    } catch {
      return false
    }
  }

  private getNaturalNavigationReferrer(): ReferrerData | false {
    if (!document.referrer) return false
    return this.parseReferrerUrl(document.referrer)
  }

  private getProgrammaticNavigationReferrer(): ReferrerData | false {
    if (!this.previousPath) return false
    return this.parseReferrerUrl(this.previousPath)
  }

  private getReferrerData(): ReferrerData | false {
    if (this.getProgrammaticNavigationReferrer())
      return this.getProgrammaticNavigationReferrer()

    if (this.getNaturalNavigationReferrer())
      return this.getNaturalNavigationReferrer()

    return false
  }
}

export const mixpanelService = MixpanelService.getInstance()
